// Status options for interview filtering
export const STATUS_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'pending', label: 'Scheduled' },
    { value: 'completed', label: 'Completed' },
    { value: 'expired', label: 'Expired' },
    { value: 'in_progress', label: 'In Progress' },
];

// Sorting options
export const SORTING_OPTIONS = [
    { value: '-started_at', label: 'Latest First' },
    { value: 'started_at', label: 'Oldest First' },
    { value: '-total_score', label: 'Highest Score' },
    { value: 'total_score', label: 'Lowest Score' },
    { value: 'first_name', label: 'Name A-Z' },
    { value: '-first_name', label: 'Name Z-A' },
    { value: '-ended_at', label: 'Recently Completed' }
];

// Page size options
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Default pagination state
export const DEFAULT_PAGINATION = {
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next: false,
    has_previous: false,
    page_size: 50
};

// Default filter state
export const DEFAULT_FILTERS = {
    search: '',
    company_id: '',
    department_id: '',
    job_description_id: '',
    status: '',
    ordering: '-started_at'
};

// Date formatting utility
export const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Score formatting utility
export const formatScore = (score) => {
    if (score === null || score === undefined) return '—';
    return `${score.toFixed(1)}/10`;
};

// Duration formatting utility
export const formatDuration = (minutes) => {
    if (!minutes) return '—';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

// Get score color class
export const getScoreColorClass = (score) => {
    if (score >= 7) return 'text-green-600';
    if (score >= 5) return 'text-yellow-600';
    if (score >= 0) return 'text-red-600';
    return 'text-gray-500';
};

// Get score label
export const getScoreLabel = (score) => {
    if (score >= 7) return 'Excellent';
    if (score >= 5) return 'Good';
    if (score >= 0) return 'Needs Improvement';
    return '';
};

// Status configuration
export const getStatusConfig = (status) => {
    const configs = {
        completed: {
            bgColor: 'bg-green-100',
            textColor: 'text-green-800',
            iconColor: 'text-green-600',
            label: 'Completed'
        },
        'in_progress': {
            bgColor: 'bg-blue-100',
            textColor: 'text-blue-800',
            iconColor: 'text-blue-600',
            label: 'In Progress'
        },
        pending: {
            bgColor: 'bg-yellow-100',
            textColor: 'text-yellow-800',
            iconColor: 'text-yellow-600',
            label: 'Scheduled'
        },
        expired: {
            bgColor: 'bg-red-100',
            textColor: 'text-red-800',
            iconColor: 'text-red-600',
            label: 'Expired'
        }
    };

    return configs[status] || {
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        iconColor: 'text-gray-600',
        label: status || 'Unknown'
    };
};

// Truncate text utility
export const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

// Search highlight utility
export const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200">$1</mark>');
};

// Validate email utility
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

// Generate unique row identifier that supports sessions, invites, and multi-JD records
export const getInterviewRowId = (candidate) => {
    if (!candidate) return '';
    // If it's an interview session with a valid session_id
    if (candidate.session_id !== null && candidate.session_id !== undefined && candidate.session_id !== '') {
        return `session_${candidate.session_id}`;
    }
    // If it's an interview invite with a valid invite_id
    if (candidate.invite_id !== null && candidate.invite_id !== undefined && candidate.invite_id !== '') {
        return `invite_${candidate.invite_id}`;
    }
    // If candidate has candidate_id and job_description_id (differentiates multiple JDs for same candidate)
    if (candidate.candidate_id && candidate.job_description_id) {
        return `cand_${candidate.candidate_id}_jd_${candidate.job_description_id}`;
    }
    if (candidate.id !== null && candidate.id !== undefined && candidate.id !== '') {
        return `id_${candidate.id}`;
    }
    if (candidate.candidate_id !== null && candidate.candidate_id !== undefined && candidate.candidate_id !== '') {
        return `cand_${candidate.candidate_id}`;
    }
    return `row_${Math.random()}`;
};

// Generate unique key for table rows
export const generateRowKey = (candidate) => {
    return getInterviewRowId(candidate);
};

// API endpoint builder
export const buildApiEndpoint = (baseUrl, filters, page, pageSize) => {
    const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
    });

    // Add filters if present
    Object.entries(filters).forEach(([key, value]) => {
        if (value && value.toString().trim()) {
            params.append(key, value.toString().trim());
        }
    });

    return `${baseUrl}?${params.toString()}`;
};

// Export all utilities as a single object for easier importing
export const InterviewUtils = {
    STATUS_OPTIONS,
    SORTING_OPTIONS,
    PAGE_SIZE_OPTIONS,
    DEFAULT_PAGINATION,
    DEFAULT_FILTERS,
    formatDate,
    formatScore,
    formatDuration,
    getScoreColorClass,
    getScoreLabel,
    getStatusConfig,
    truncateText,
    highlightSearchTerm,
    isValidEmail,
    getInterviewRowId,
    generateRowKey,
    buildApiEndpoint
};

export default InterviewUtils;